/**
 * @file     FlowStepHeader.tsx
 * @readme   ../README.md
 * @purpose  The step-flow header: Back on the left whenever there is a step to return to, the count ("2 of 4") on the right, and a segmented bar under both. It sits outside the step transition so it never animates.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { flowColors, flowFocusRing, flowRadius, flowTransition, reducedMotion, visuallyHidden } from './flowKitTokens';

type FlowStepHeaderProps = {
  canGoBack: boolean;
  onBack: () => void;
  /** Omit both to show Back alone (result steps outside the numbered path). */
  current?: number;
  total?: number;
};

export const FlowStepHeader = ({ canGoBack, onBack, current, total }: FlowStepHeaderProps) => {
  const { t } = useTranslation();
  const showCount = typeof current === 'number' && typeof total === 'number' && total > 0;
  if (!canGoBack && !showCount) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, minHeight: canGoBack ? 44 : 0 }}>
        {canGoBack ? (
          <Box
            component="button"
            type="button"
            onClick={onBack}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              minHeight: 44,
              ml: -3,
              px: 3,
              border: 0,
              borderRadius: `${flowRadius.md}px`,
              background: 'none',
              color: flowColors.textSecondary,
              font: 'inherit',
              fontSize: 14.5,
              fontWeight: 500,
              cursor: 'pointer',
              transition: flowTransition('color', 'background-color'),
              '&:hover': { color: flowColors.textPrimary, backgroundColor: flowColors.surfaceSunken },
              ...flowFocusRing,
              [reducedMotion]: { transition: 'none' },
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            {t('dealFinder.actions.back', { defaultValue: 'Back' })}
          </Box>
        ) : (
          <span />
        )}
        {showCount ? (
          <Typography
            component="p"
            data-testid="flow-step-count"
            sx={{
              m: 0,
              fontSize: 13,
              fontWeight: 500,
              color: flowColors.textMuted,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            <Box component="span" sx={visuallyHidden}>
              {t('leadFlow.progress.stepPrefix', { defaultValue: 'Step' })}{' '}
            </Box>
            {t('leadFlow.progress.count', {
              defaultValue: '{{current}} of {{total}}',
              current,
              total,
            })}
          </Typography>
        ) : null}
      </Box>
      {showCount ? (
        <Box aria-hidden="true" sx={{ display: 'flex', gap: '4px' }}>
          {Array.from({ length: total }, (_, index) => (
            <Box
              key={index}
              component="span"
              sx={{
                flex: 1,
                height: 3,
                borderRadius: `${flowRadius.pill}px`,
                backgroundColor: index < (current ?? 0) ? flowColors.interactivePrimary : flowColors.borderSubtle,
                transition: flowTransition('background-color'),
                [reducedMotion]: { transition: 'none' },
              }}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
};
